import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getVocDetail, vocFileDownload } from "../../utils/api";

interface VocData {
  no: number;
  name: string;
  phoneNumber: string;
  email: string;
  title: string;
  content: string;
  fileOriginName?: string;
  filePath?: string;
  fileName?: string;
  dateTime: string;
  accept: string; // "1": 접수대기, "2": 접수완료, "3": 조치중, "4": 완료
  serviceSortation: string;
  password: string;
  oriDateTime: string;
}

const STATUS_INFO: Record<string, { name: string; action: string }> = {
  "1": { name: "접수 대기중", action: "접수 대기중입니다." },
  "2": { name: "접수 완료", action: "접수가 완료되었습니다." },
  "3": { name: "조치중", action: "담당자가 조치중입니다." },
  "4": { name: "처리 완료", action: "처리가 완료되었습니다." },
};

const VocView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { postNumber, password, contact } =
    (location.state as { postNumber?: string; password?: string; contact?: string }) || {};

  const [vocData, setVocData] = useState<VocData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const handleFileDownload = async () => {
    if (!vocData || !vocData.filePath) return;

    try {
      setDownloading(true);
      await vocFileDownload(vocData.no, vocData.fileOriginName || vocData.fileName || "첨부파일");
    } catch (err) {
      console.error("파일 다운로드 실패:", err);
      alert("파일 다운로드에 실패했습니다.");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (!postNumber || !password || !contact) {
      navigate("/voc/check");
      return;
    }

    const fetchVocData = async () => {
      try {
        setLoading(true);
        const response = await getVocDetail(postNumber, password, contact);
        console.log("API Response:", response);
        setVocData(response);
      } catch (err) {
        setError("민원 정보를 불러오는데 실패했습니다.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchVocData();
  }, [postNumber, password, contact, navigate]);

  if (loading) {
    return (
      <main id="main-content">
        <div
          className="flex items-center justify-center mx-auto"
          style={{
            maxWidth: "var(--container-max-width, 1200px)",
            margin: "150px auto",
            padding: "0 var(--page-padding-x, 2rem)",
            minHeight: "400px",
          }}
        >
          <p style={{ fontSize: "var(--font-title-medium, 1.6rem)", color: "#666" }}>로딩 중...</p>
        </div>
      </main>
    );
  }

  if (error || !vocData) {
    return (
      <main id="main-content">
        <div
          className="mx-auto"
          style={{
            maxWidth: "var(--container-max-width, 1200px)",
            margin: "150px auto",
            padding: "0 var(--page-padding-x, 2rem)",
          }}
        >
          <div className="text-center" style={{ padding: "60px 0" }}>
            <p
              style={{
                fontSize: "var(--font-title-medium, 1.6rem)",
                color: "#666",
                marginBottom: "20px",
              }}
            >
              {error || "민원 정보를 찾을 수 없습니다."}
            </p>
            <Link
              to="/voc/check"
              className="inline-block transition-all hover:bg-[#1a1a1a]"
              style={{
                padding: "12px 24px",
                backgroundColor: "#333",
                color: "#fff",
                borderRadius: "var(--radius-sm, 4px)",
                fontSize: "var(--font-title-small, 1.5rem)",
                textDecoration: "none",
              }}
            >
              조회 페이지로 돌아가기
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const statusInfo = STATUS_INFO[vocData.accept] || STATUS_INFO["1"];

  return (
    <main id="main-content">
      <div
        className="mx-auto"
        style={{
          maxWidth: "var(--container-max-width, 1200px)",
          margin: "150px auto",
          padding: "0 var(--page-padding-x, 2rem)",
        }}
      >
        {/* Title Box */}
        <div
          className="flex justify-between items-center pb-[30px] mb-[30px]"
          style={{ borderBottom: "1px solid #e5e5e5" }}
        >
          <div>
            <h1
              className="inline-block mb-[15px]"
              style={{
                fontSize: "var(--font-headline-large, 3.2rem)",
                fontWeight: "bold",
                color: "#1a1a1a",
              }}
            >
              민원 조회하기 ⬥
            </h1>
            <p
              style={{
                fontSize: "var(--font-title-large, 1.8rem)",
                color: "#1a1a1a",
              }}
            >
              문의한 내용을 조회할 수 있습니다.
            </p>
          </div>
          <div>
            <Link
              to="/voc/check"
              className="flex items-center justify-between transition-all hover:bg-[#ececec] hover:-translate-y-0.5"
              style={{
                width: "120px",
                minHeight: "44px",
                padding: "0.5rem 1rem",
                border: "1px solid #888",
                borderRadius: "var(--radius-sm, 4px)",
                backgroundColor: "#fff",
                fontSize: "var(--font-title-small, 1.5rem)",
                color: "#1a1a1a",
                textDecoration: "none",
              }}
            >
              이전
              <span className="material-icons-round">arrow_back</span>
            </Link>
          </div>
        </div>

        {/* Status Section */}
        <div
          className="mb-[40px] p-[40px]"
          style={{
            backgroundColor: "#f8f9fa",
            borderRadius: "var(--radius-md, 8px)",
          }}
        >
          <div className="text-center mb-[30px]">
            <p
              style={{
                fontSize: "var(--font-title-medium, 1.6rem)",
                color: "#666",
                marginBottom: "8px",
              }}
            >
              현재 '<span style={{ color: "#0066cc", fontWeight: 600 }}>{statusInfo.name}</span>'
              상태입니다.
            </p>
            <h2
              style={{
                fontSize: "var(--font-headline-small, 2.4rem)",
                fontWeight: "bold",
                color: "#1a1a1a",
              }}
            >
              고객님의 문의는 <span style={{ color: "#0066cc" }}>{statusInfo.action}</span>
            </h2>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-0">
            {[
              { step: "1", label: "문의" },
              { step: "2", label: "접수" },
              { step: "3", label: "조치" },
              { step: "4", label: "완료" },
            ].map((item, index) => (
              <div key={item.step} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className="flex items-center justify-center"
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "50%",
                      backgroundColor:
                        parseInt(vocData.accept) >= parseInt(item.step) ? "#0066cc" : "#ddd",
                      color: "#fff",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <span className="material-icons-round">check</span>
                  </div>
                  <p
                    style={{
                      marginTop: "8px",
                      fontSize: "var(--font-title-small, 1.5rem)",
                      fontWeight: parseInt(vocData.accept) >= parseInt(item.step) ? 600 : 400,
                      color: parseInt(vocData.accept) >= parseInt(item.step) ? "#0066cc" : "#999",
                    }}
                  >
                    {item.label}
                  </p>
                </div>
                {index < 3 && (
                  <div
                    style={{
                      width: "80px",
                      height: "2px",
                      backgroundColor:
                        parseInt(vocData.accept) > parseInt(item.step) ? "#0066cc" : "#ddd",
                      margin: "0 8px",
                      marginBottom: "28px",
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Detail Content */}
        <div>
          <h3
            style={{
              fontSize: "var(--font-title-large, 1.8rem)",
              fontWeight: 600,
              color: "#1a1a1a",
              marginBottom: "20px",
            }}
          >
            <span style={{ color: "#0066cc" }}>{vocData.name}</span>님의 민원 내역입니다.
          </h3>

          <div
            style={{
              border: "1px solid #e5e5e5",
              borderRadius: "var(--radius-md, 8px)",
              overflow: "hidden",
            }}
          >
            <table className="w-full" style={{ borderCollapse: "collapse" }}>
              <colgroup>
                <col style={{ width: "160px" }} />
                <col style={{ width: "auto" }} />
              </colgroup>
              <tbody>
                {[
                  { label: "작성자", value: vocData.name },
                  { label: "게시글 번호", value: vocData.no },
                  { label: "연락처", value: vocData.phoneNumber },
                  { label: "이메일", value: vocData.email },
                  { label: "제목", value: vocData.title },
                ].map(row => (
                  <tr key={row.label} style={{ borderBottom: "1px solid #e5e5e5" }}>
                    <th
                      style={{
                        padding: "16px 20px",
                        backgroundColor: "#f8f9fa",
                        fontSize: "var(--font-title-small, 1.5rem)",
                        fontWeight: 500,
                        color: "#666",
                        textAlign: "left",
                        borderRight: "1px solid #e5e5e5",
                      }}
                    >
                      {row.label}
                    </th>
                    <td
                      style={{
                        padding: "16px 20px",
                        fontSize: "var(--font-title-small, 1.5rem)",
                        color: "#1a1a1a",
                      }}
                    >
                      {row.value}
                    </td>
                  </tr>
                ))}
                <tr style={{ borderBottom: "1px solid #e5e5e5" }}>
                  <th
                    style={{
                      padding: "16px 20px",
                      backgroundColor: "#f8f9fa",
                      fontSize: "var(--font-title-small, 1.5rem)",
                      fontWeight: 500,
                      color: "#666",
                      textAlign: "left",
                      verticalAlign: "top",
                      borderRight: "1px solid #e5e5e5",
                    }}
                  >
                    내용
                  </th>
                  <td
                    style={{
                      padding: "16px 20px",
                      fontSize: "var(--font-title-small, 1.5rem)",
                      color: "#1a1a1a",
                      lineHeight: 1.8,
                      minHeight: "120px",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {vocData.content}
                  </td>
                </tr>
                <tr>
                  <th
                    style={{
                      padding: "16px 20px",
                      backgroundColor: "#f8f9fa",
                      fontSize: "var(--font-title-small, 1.5rem)",
                      fontWeight: 500,
                      color: "#666",
                      textAlign: "left",
                      borderRight: "1px solid #e5e5e5",
                    }}
                  >
                    첨부파일
                  </th>
                  <td
                    style={{
                      padding: "16px 20px",
                      fontSize: "var(--font-title-small, 1.5rem)",
                      color: "#1a1a1a",
                    }}
                  >
                    {vocData.filePath ? (
                      <button
                        onClick={handleFileDownload}
                        disabled={downloading}
                        className="hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          color: "#0066cc",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                          fontSize: "inherit",
                        }}
                      >
                        {downloading
                          ? "다운로드 중..."
                          : vocData.fileOriginName || vocData.fileName || "첨부파일 다운로드"}
                      </button>
                    ) : (
                      <span style={{ color: "#999" }}>첨부 없음</span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p
            style={{
              marginTop: "16px",
              fontSize: "var(--font-body-medium, 1.4rem)",
              color: "#666",
              textAlign: "right",
            }}
          >
            문의 날짜 : <span>{vocData.dateTime}</span>
          </p>
        </div>
      </div>
    </main>
  );
};

export default VocView;
